<template>
    <div>
        <div
            v-if="isEmpty && !isLoading"
            class="text-center text-lg font-bold"
        >
            <span>{{ emptyMessage }}</span>
        </div>
        <transition-group
            name="list"
            tag="div"
            @after-enter="notifyImpression"
        >
            <div
                v-for="item in displayList"
                :key="item.id"
                :data-index="item.id"
                class="mb-4"
            >
                <JobPost
                    :job-info="item"
                    :company-profile-widget="companyProfileWidget"
                    :data-test-root="`${testIdRoot}`"
                    :is-from-saved-list="isSavedList"
                    @update-vibe="updateVibe"
                    @application-interest-clicked="applicationInterestClicked"
                />
            </div>
        </transition-group>
        <div class="flex justify-center my-12">
            <Spinner v-if="isLoading"></Spinner>
            <!-- native modifier has been removed, please confirm whether the function has been affected  -->
            <Btn
                v-else-if="disabled"
                :data-testid="`${testIdRoot}-load-more-btn`"
                :class="[
                    'my-2 border border-blue-300',
                    {
                        'w-full': screenSize === 'small',
                    },
                ]"
                variant="tonal"
                color="primary"
                :label="t('job_list.load_more')"
                @click="loadMore"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isEmpty } from 'lodash-es';
import { trackEvent } from '@/modules/tracking';
import { useTranslation } from 'i18next-vue';
import JobPost from './JobPost.vue';

const STATUS_STDR = 1;
const STATUS_STST_ACTIVE = 2;
const ACTION_VIBED = 1;
const ACTION_DISCARDED = 2;
export default {
    name: 'JobList',

    components: {
        JobPost,
    },

    props: {
        jobList: {
            type: Array,
            required: true,
        },
        progress: {
            type: Boolean,
            required: true,
        },
        emptyMessage: {
            type: String,
            default: '',
        },
        canLoadMore: {
            type: Boolean,
            default: false,
        },
        companyProfileWidget: {
            type: Boolean,
            required: false,
        },
        testIdRoot: {
            type: String,
            required: true,
        },
        isSavedList: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['get-more-data', 'application-interest-clicked'],

    setup() {
        const { t } = useTranslation('career-corner');
        return { t };
    },

    data() {
        return {
            page: 1,
        };
    },

    computed: {
        ...mapGetters({
            screenSize: 'ui/screenSize',
        }),
        isLoading() {
            return this.progress;
        },
        isEmpty() {
            return isEmpty(this.jobList);
        },
        disabled() {
            return !this.isEmpty && !this.isLoading && this.canLoadMore;
        },
        displayList() {
            const count = 10;
            return this.jobList.slice(0, (this.page + 1) * count);
        },
    },

    methods: {
        loadMore() {
            if (this.isLoading) return;
            this.page += 1;
            this.$emit('get-more-data', this.page);
        },
        updateVibe(id, vibed) {
            const job = this.jobList.find((j) => j.id === id);
            if (job !== null) {
                const data = {
                    job_id: id,
                    action: vibed ? ACTION_VIBED : ACTION_DISCARDED,
                    status: job.isStSt ? STATUS_STST_ACTIVE : STATUS_STDR,
                };
                this.$store.dispatch('careerCorner/saveJobVibeAction', {
                    isSwipedJob: false,
                    data,
                });
                this.track(vibed ? 'Vibe' : 'Unvibe', id);
            }
        },
        async track(trackingName, jobId = null) {
            if (jobId == null) {
                trackEvent('CareerMatching', trackingName);
            } else {
                trackEvent('CareerMatching', trackingName, jobId);
            }
        },
        applicationInterestClicked() {
            this.$emit('application-interest-clicked');
        },
        notifyImpression(element) {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && element.dataset.index) {
                        const jobId = parseInt(element.dataset.index, 10);
                        trackEvent('CareerMatching', 'JobListImpression', jobId);
                        observer.unobserve(element);
                    }
                },
                { threshold: [0] },
            );
            observer.observe(element);
        },
    },
};
</script>
<style lang="scss" scoped>
.list-enter-active,
.list-leave-active {
    max-height: 350px;
    transition: all 0.5s ease;
}
.list-enter-from,
.list-leave-to {
    max-height: 0;
    opacity: 0;
}
</style>
